/**
 * Browser Candle Tracker
 * Replicates candle-live.js functionality for browser environment
 * 
 * Key differences from Electron:
 * - No child process (runs in main thread)
 * - Uses Hyperliquid SDK directly (same as Electron worker)
 * - Emits CustomEvent instead of stdout
 * - Singleton pattern to ensure only one instance per asset
 */

import { WebSocketTransport, SubscriptionClient, HttpTransport, InfoClient } from '@nktkas/hyperliquid';

export class BrowserCandleTracker {
    constructor() {
        this.transport = null;
        this.subsClient = null;
        this.infoClient = null;
        this.subscription = null;
        this.currentAsset = null;
        this.interval = '15m';
        this.isConnected = false;
        this.isRunning = false;
        this.lastUpdate = null;
        this.candleData = [];
        this.currentCandle = null;
    }

    /**
     * Start candle tracking for specified asset
     * @param {string} asset - Asset symbol (e.g., 'BTC', 'ETH')
     * @param {string} interval - Candle interval (default: '15m')
     */
    async start(asset, interval = '15m') {
        if (this.isRunning && this.currentAsset === asset && this.interval === interval) {
            // console.log(`[CANDLE-TRACKER] Already running for ${asset} ${interval}`);
            return;
        }

        // Stop existing subscription if switching assets/interval
        if (this.isRunning) {
            await this.stop();
        }

        try {
            // console.log(`[CANDLE-TRACKER] Starting for ${asset} ${interval}`);
            // Don't uppercase HIP-3 assets (xyz:NVDA must stay lowercase prefix)
            this.currentAsset = asset.includes(':') ? asset : asset.toUpperCase();
            this.interval = interval;

            // Create WebSocket transport (same config as candle-live.js)
            this.transport = new WebSocketTransport({
                url: 'wss://api.hyperliquid.xyz/ws',
                timeout: 10000,
                keepAlive: {
                    interval: 30000,
                    timeout: 10000
                },
                autoResubscribe: true,
                reconnect: {
                    maxAttempts: Infinity,
                    delay: 1000,
                    maxDelay: 30000,
                    factor: 1.5
                }
            });

            // Create subscription client
            this.subsClient = new SubscriptionClient({ transport: this.transport });

            // Create HTTP client for initial data
            const httpTransport = new HttpTransport();
            this.infoClient = new InfoClient({ transport: httpTransport });

            // Wait for connection
            await this.transport.ready();
            this.isConnected = true;
            // console.log(`[CANDLE-TRACKER] Connected to WebSocket`);

            // Load initial historical data (matches candle-live.js line 121-122)
            await this.loadInitialData();

            // Subscribe to live candle updates
            await this.subscribeToCandles();

            this.isRunning = true;

        } catch (error) {
            // console.error(`[CANDLE-TRACKER] Failed to start:`, error);
            throw error;
        }
    }

    /**
     * Load initial historical candle data
     * Matches candle-live.js loadInitialData() method (lines 137-195)
     */
    async loadInitialData() {
        try {
            // console.log(`[CANDLE-TRACKER] Loading initial data for ${this.currentAsset} ${this.interval}`);

            const now = Date.now();

            // Calculate historical range for all 14 Hyperliquid timeframes
            let historicalDays;
            switch (this.interval) {
                case '1m':
                    historicalDays = 1;
                    break;
                case '3m':
                    historicalDays = 2;
                    break;
                case '5m':
                    historicalDays = 7;
                    break;
                case '15m':
                    historicalDays = 14; // 2 weeks for better chart context
                    break;
                case '30m':
                    historicalDays = 30; // 1 month for better chart context
                    break;
                case '1h':
                    historicalDays = 60;
                    break;
                case '2h':
                    historicalDays = 90;
                    break;
                case '4h':
                    historicalDays = 180;
                    break;
                case '8h':
                    historicalDays = 270;
                    break;
                case '12h':
                    historicalDays = 365;
                    break;
                case '1d':
                    historicalDays = 365;
                    break;
                case '3d':
                    historicalDays = 730;
                    break;
                case '1w':
                    historicalDays = 1095; // 3 years
                    break;
                case '1M':
                    historicalDays = 1825; // 5 years
                    break;
                default:
                    historicalDays = 1;
            }

            const startTime = now - (historicalDays * 24 * 60 * 60 * 1000);

            // Fetch historical candles
            const candles = await this.infoClient.candleSnapshot({
                coin: this.currentAsset,
                interval: this.interval,
                startTime: startTime,
                endTime: now
            });

            if (candles && Array.isArray(candles)) {
                this.candleData = candles;
                // console.log(`[CANDLE-TRACKER] Loaded ${candles.length} historical candles`);

                // Set current candle to most recent
                if (candles.length > 0) {
                    this.currentCandle = candles[candles.length - 1];
                    // Broadcast initial data to chart immediately
                    this.broadcastCandle();
                }
            }

        } catch (error) {
            // console.error('[CANDLE-TRACKER] Error loading initial data:', error);
        }
    }

    /**
     * Subscribe to live candle updates
     * Matches candle-live.js subscribeToCandles() method (lines 200-218)
     */
    async subscribeToCandles() {
        try {
            // Defensive check: subsClient should never be null here, but check anyway
            if (!this.subsClient) {
                throw new Error('[CANDLE-TRACKER] subsClient is null - tracker not properly initialized');
            }
            
            this.subscription = await this.subsClient.candle(
                {
                    coin: this.currentAsset,
                    interval: this.interval
                },
                (candleData) => this.handleCandleUpdate(candleData)
            );

            // console.log(`[CANDLE-TRACKER] Subscribed to ${this.currentAsset} ${this.interval} candles`);

        } catch (error) {
            // console.error('[CANDLE-TRACKER] Failed to subscribe:', error);
            throw error;
        }
    }

    /**
     * Handle incoming candle updates
     * Matches candle-live.js handleCandleUpdate() method (lines 224-252)
     */
    handleCandleUpdate(candleData) {
        try {
            // Ignore updates if not running or no asset selected (prevent stale data during asset switch)
            if (!this.isRunning || !this.currentAsset) {
                return;
            }

            this.currentCandle = candleData;
            this.lastUpdate = new Date().toISOString();

            // Update local candle data
            const existingIndex = this.candleData.findIndex(c => c.t === candleData.t);
            if (existingIndex >= 0) {
                // Update existing candle
                this.candleData[existingIndex] = candleData;
                // console.log(`📊 [CANDLE-TRACKER] Updating existing candle for ${this.currentAsset}:`, {
                //     time: new Date(candleData.t).toISOString(),
                //     close: candleData.c,
                //     isIntraCandle: true
                // });
            } else {
                // Add new candle
                this.candleData.push(candleData);
                // console.log(`📊 [CANDLE-TRACKER] New candle for ${this.currentAsset}:`, {
                //     time: new Date(candleData.t).toISOString(),
                //     close: candleData.c,
                //     isNewCandle: true
                // });

                // Keep only last 100 candles for performance
                if (this.candleData.length > 100) {
                    this.candleData = this.candleData.slice(-100);
                }
            }

            // Broadcast to Terminal view
            this.broadcastCandle();

        } catch (error) {
            // console.error('[CANDLE-TRACKER] Error processing update:', error);
        }
    }

    /**
     * Broadcast candle data to Terminal view
     * Matches candle-live.js sendToDashboard() payload format (lines 377-385)
     */
    broadcastCandle() {
        try {
            // Don't broadcast if not running or no asset (prevent stale data)
            if (!this.isRunning || !this.currentAsset || !this.currentCandle) {
                return;
            }

            const payload = {
                type: 'candle-update',
                asset: this.currentAsset,
                interval: this.interval,
                candle: this.currentCandle,
                historicalData: this.candleData.slice(-100), // Send last 100 candles
                timestamp: Date.now(),
                connected: this.isConnected
            };

            // Emit CustomEvent instead of stdout
            window.dispatchEvent(new CustomEvent('candle-data', {
                detail: payload
            }));

        } catch (error) {
            // console.error('[CANDLE-TRACKER] Error broadcasting:', error);
        }
    }

    /**
     * Stop candle tracking
     */
    async stop() {
        try {
            // console.log(`[CANDLE-TRACKER] Stopping for ${this.currentAsset}`);

            // Set flags first to prevent any pending callbacks from broadcasting
            this.isRunning = false;
            this.isConnected = false;
            const oldAsset = this.currentAsset;
            this.currentAsset = null;
            this.currentCandle = null;

            if (this.subscription) {
                // console.log(`[CANDLE-TRACKER] Unsubscribing from ${oldAsset}`);
                await this.subscription.unsubscribe();
                this.subscription = null;
            }

            if (this.subsClient) {
                this.subsClient = null;
            }

            // CRITICAL: Close the WebSocket transport to stop ALL data flow
            if (this.transport) {
                // console.log(`[CANDLE-TRACKER] Closing WebSocket transport for ${oldAsset}`);
                await this.transport.close();
                this.transport = null;
            }

            this.candleData = [];

            // console.log(`[CANDLE-TRACKER] Stopped ${oldAsset}`);

        } catch (error) {
            // console.error('[CANDLE-TRACKER] Error stopping:', error);
        }
    }

    /**
     * Change asset (stop current, start new)
     */
    async changeAsset(newAsset, newInterval) {
        await this.stop();
        await this.start(newAsset, newInterval || this.interval);
    }
}

// Create singleton instance
if (typeof window !== 'undefined') {
    window.BrowserCandleTracker = new BrowserCandleTracker();
    // console.log('✅ BrowserCandleTracker initialized');
}

export default BrowserCandleTracker;
